import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../common/Logo";
import Avatar from "../common/Avatar";
import Icon from "../common/Icon";
import { mainNavItems } from "../../data/navigation";
import { useAuth } from "../../context/AuthContext";
import { suggestedContacts } from "../../data/users";
import { MessageSquare } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import { getAssetUrl } from "../../services/helper";
const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");
const SuggestedContacts = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch Suggested / Connected Users
  const { data, isLoading, error } = useQuery({
    queryKey: ["connected-contacts"],
    queryFn: () => api.fetchConnectedContact(""),
    staleTime: 5 * 60 * 1000,
  });

  const contacts = data?.data || [];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1 px-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-3 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gray-200 rounded-full" />
              <div className="space-y-1.5">
                <div className="h-4 w-36 bg-gray-200 rounded" />
                <div className="h-3 w-20 bg-gray-200 rounded" />
              </div>
            </div>
            <div className="w-10 h-10 bg-gray-200 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-500 text-sm">Failed to load suggested contacts</div>;
  }

  return (
    <div className="flex flex-col gap-1">
      {contacts.slice(0,3).map((contact) => (
        <div
          key={contact.id}
          className="flex items-center justify-between px-3 py-1 rounded-xl hover:bg-gray-50 transition-colors"
        >
          {/* Avatar + User Info */}
          <div
            className="flex items-center gap-3 cursor-pointer min-w-0 flex-1"
            onClick={() => navigate(`/other-profile/${contact.id}`)}
          >
            <Avatar
              src={getAssetUrl(contact.avatar)}
              alt={contact.name}
              size={47}
              className="flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <p className="text-[14.25px] font-semibold text-[#111111] truncate">
                {contact.name}
              </p>
              <p className="text-[11px] text-[#657786] capitalize">
                suggested
              </p>
              {/* {contact.badge && (
                <span className="inline-block text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                  {contact.badge}
                </span>
              )} */}
            </div>
          </div>

          {/* Message Button */}
          <div
            className="w-10 h-10 rounded-[28.33px] bg-[#1F314733] flex items-center justify-center cursor-pointer transition-colors flex-shrink-0 ml-2"
            onClick={() => navigate(`/messages?chat_id=${contact.id}`)}
          >
            <MessageSquare size={16} className="text-[#1F3A60]" />
          </div>
        </div>
      ))}

      {contacts.length === 0 && (
        <p className="text-center text-gray-500 py-6">No suggested contacts available</p>
      )}
    </div>
  );
};
const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleNav = (path) => {
    navigate(path);
    onClose?.();
  };

  return (
    <aside className="w-[320px] lg:w-[300px] xl:w-[350px] h-screen bg-white flex flex-col border-r border-gray-100 flex-shrink-0 overflow-y-auto scrollbar-thin">
      <div className="flex items-center justify-between px-4 py-6 flex-shrink-0">
        <div className="flex-1 flex justify-center">
          <Logo size={110} />
        </div>
        {/* Close button — only on mobile */}
        <button
          onClick={onClose}
          className="lg:hidden w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 flex-shrink-0"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6B7280"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div className="border-t border-[#00000026] mx-8 flex-shrink-0" />

      <div className="px-8 pt-8 flex-shrink-0">
        <p className="text-[13.67px] font-medium text-[#757575] font-body tracking-[2px] uppercase mb-2 px-3">
          Main
        </p>
        <nav className="flex flex-col gap-0.5">
          {mainNavItems.map((item) => {
            const showLock = item.premiumLock && user && user.role === "free";

            if (user?.role === "free" && (item?.id == "message" || item?.id == "mentorship" )) return;


            return (
              <button
                key={item.id}
                onClick={() => {
                  if (!showLock) handleNav(item.path);
                }}
                className={`flex items-center gap-3 px-4 py-[17px] rounded-xl text-[16px] font-medium w-full text-left transition-all duration-150 font-body ${isActive(item.path)
                  ? "bg-[#1F3A60] text-white"
                  : showLock
                    ? "bg-gray-200 text-[#757575] cursor-not-allowed"
                    : "text-[#757575] hover:bg-gray-50"
                  }`}
              >
                <span
                  className={
                    isActive(item.path)
                      ? "text-white opacity-90"
                      : showLock
                        ? "text-gray-400"
                        : "text-gray-400"
                  }
                >
                  <Icon name={item.icon} size={20} />
                </span>
                <span className="flex-1">{item.label}</span>
                {showLock && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="flex-shrink-0"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                )}
              </button>
            );
          })}
        </nav>
      </div>
      <div className="border-t border-[#00000026] mx-8 mt-4 flex-shrink-0" />
      <div className="px-6 pt-4 flex-shrink-0">
        <div class="flex justify-between items-center pb-2">
          <p className="text-[13.67px] font-medium text-[#757575] tracking-[2px] uppercase px-3"> Contacts</p>
          <button
            onClick={() => navigate(`/contacts`)}

            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors disabled:opacity-70"
          >
            View All
          </button>
        </div>


        <SuggestedContacts />
      </div>

      {user && <div className="border-t border-gray-200 mx-8 mt-4 flex-shrink-0" />}
      {user && (
        <div className="px-8 py-4 flex-shrink-0">
          <div className="flex items-center justify-between px-3 py-2">
            <div
              className="flex items-center gap-3 min-w-0 cursor-pointer"
              onClick={() => {
                navigate("/profile");
              }}
            >
              <Avatar
                src={
                  user.avatar
                    ? user.avatar.startsWith("/uploads")
                      ? `${API_ROOT}${user.avatar}`
                      : user.avatar
                    : "/assets/images/avatar-brother-julian.png"
                }
                alt={user.name}
                size={36}
                className="flex-shrink-0"
              />
              <div className="min-w-0">
                <p className="text-[13px] font-bold text-gray-800 truncate">
                  {user.name}
                </p>
                <p className="text-[11px] text-gray-400 capitalize">
                  {user.role} member
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center flex-shrink-0"
              title="Logout"
            >
              <Icon name="logout" size={16} className="text-gray-400" />
            </button>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
